import React, {useEffect, useRef, useState} from "react";
import {Layout, Menu} from "antd";
import {Navbar} from "@components/Navigation/Navbar";
import {AdminNotice} from "@components/Settings/AdminNotice";
import HelpButton from "@components/Common/HelpButton";
import {useSelector} from "react-redux";
import {useAuth0} from "@auth0/auth0-react";
import { isStaffEmail } from "@utils/user_utils";
import "../../Styles/styles-Chat.css";
import {Content, Footer, Header} from "antd/es/layout/layout";
import ChatHeader from "@components/Chatbot/Header";
import { AutocompleteTextarea } from "@components/Common/AutocompleteTextarea";
import Sidebar from "@components/Chatbot/Sidebar";
import {ChatbotProvider, useChatbotContext} from "@components/Chatbot/context";
import MessageDisplay from "@components/Chatbot/Messages";
import {TrainingAPI} from "@api/TrainingAPI";
import {isFollowUpMessage} from "@components/Chatbot/utils";
import {PremiumFeatureDisabled} from "@components/Common/PremiumFeatureDisabled";
import BlurredChatbot from "@components/Chatbot/blurred-chatbot.png";

let trainingAPI = new TrainingAPI();


const ChatInterface = () => {
    const { getAccessTokenSilently } = useAuth0();
    const eventSourceRef = useRef(null); // Track EventSource instance
    const {
        messages,
        appendNewMessage,
        conversationId,
        setConversationId,
        streamingUuid,
        setIsTyping,
        setIntermediateMessages,
        setFollowUpLoading,
        setFollowUpMessages
    } = useChatbotContext();

    const sendMessage = async (text, sender) => {
        appendNewMessage({
            id: Date.now(),
            created: Date.now(),
            version: "0.1",
            metadata: {},
            content: {
                message: text,
                type: "text"
            },
            sender: sender
        });
        setIsTyping(true);

        const res = await trainingAPI.callChatbotApi({
            inboundMessage: text,
            messages: conversationId ? [] : messages,
            conversationId,
            streamingUuid
        });

        appendNewMessage({
            created: Date.now(),
            id: Date.now(),
            content: res.outbound_content,
            metadata: res.metadata,
            sender: res.sender,
            version: res.version
        });

        setConversationId(res.conversation_id)
        setIsTyping(false);
    }

    useEffect(() => {
        if (!streamingUuid) return;

        let isMounted = true;

        async function setupEventSource() {
            try {
                const token = await getAccessTokenSilently();

                if (!isMounted) return;

                const eventSource = new EventSource(`${trainingAPI.url}/rte/${streamingUuid}/?token=${token}`);
                eventSourceRef.current = eventSource;

                const handleSseEvent = (event) => {
                    const data = event?.data;
                    let parsedData;
                    try {
                        parsedData = JSON.parse(data);
                    } catch (e) {
                        console.error("Failed to parse SSE event data:", data, e);
                        return;
                    }

                    if (isFollowUpMessage(parsedData)) {
                        if (parsedData?.status === "in-progress") {
                            setFollowUpLoading(true);
                        } else if (parsedData?.status === "done") {
                            const messageString = JSON.stringify(parsedData?.message?.content);
                            const messages = JSON.parse(messageString);
                            setFollowUpLoading(false);
                            setFollowUpMessages(JSON.parse(messages).follow_up_questions);
                        }
                        return;
                    }

                    if (parsedData?.type === "render" && parsedData?.artifact?.spec_type === "chart") {
                        appendNewMessage({
                            created: Date.now(),
                            id: Date.now(),
                            content: {
                                type: "chart",
                                message: parsedData.artifact.narrative || "",
                                chart: parsedData.artifact.payload,
                            },
                            metadata: {},
                            sender: "agent",
                        });
                        return;
                    }

                    setIntermediateMessages(prev => [...prev, parsedData])
                    if (parsedData?.status === "done") {
                        setIntermediateMessages([])
                    }
                };

                eventSource.onmessage = handleSseEvent;
                eventSource.addEventListener("render", handleSseEvent);

                eventSource.onerror = (err) => {
                    eventSource.close();
                };
            } catch (err) {
                console.error("Failed to setup EventSource:", err);
            }
        }

        setupEventSource().then();

        return () => {
            isMounted = false;
            if (eventSourceRef?.current) {
                eventSourceRef?.current?.close();
            }
        };
    }, [streamingUuid, getAccessTokenSilently]);

    return (
        <Layout className="chat-interface">
            {/* Header + buttons */}
            <ChatHeader />
            <Content>
                {/* messages here */}
                <MessageDisplay
                    sendMessage={sendMessage}
                />
            </Content>
        </Layout>
    )
}


const ChatView = () => {
    const { user } = useAuth0();

    const {
        info : {dvai_enabled},
    } = useSelector(({team}) => team);

    const isStaff = isStaffEmail(user?.email);

    if (!dvai_enabled && !isStaff) {
        return (
            <PremiumFeatureDisabled
                bg_image={BlurredChatbot}
                feature_name={"DataViz AI"}
                info_text={<>
                    This is an elite feature that will allow you to talk to your data in seconds. <br/> <br/>
                    If you are interested in adding this to your platform, please contact your sales rep!</>}
            />
        )
    }

    return (
        <>
            <Navbar />
            <HelpButton page="Dataviz AI" />

            {(!dvai_enabled && isStaff) && (
                <div style={{ margin: '16px', maxWidth: '800px', marginLeft: 'auto', marginRight: 'auto' }}>
                    <AdminNotice />
                </div>
            )}
            <ChatbotProvider>
                <Layout className="chatbot-page-container">
                    <Sidebar />
                    <ChatInterface />
                </Layout>
            </ChatbotProvider>
        </>
    )
}

export default ChatView;
